import styled from "@emotion/styled";
import { IconInfo, IconPhoneCall } from "@kompare/kompare-ui-library";
import { useEffect, useState } from "react";
import { i18n, useCallMe, useGeneralSoContext } from "src/common";
import { CallMeInput } from "src/common/apiGraphql/types/types";
import { colors } from "src/theme";
import { Button } from "../buttons";
import { Validation, validationRules } from "../forms";
import { Divider, Flex } from "../grid";
import { InputPhoneNumber } from "../inputs";
import { HtmlText, HtmlTextLink } from "../other";
import { Text } from "../typography";
import BaseModal from "./base.modal";
import { mediaQuery } from "src/common";

const CallMeModalTitle = styled.div({
  paddingRight: '34px',
  [mediaQuery('tablet')]: {
    paddingRight: '0',
  }
});

export type CallMeModalProps = {
  open: boolean;
  onPhoneChange?: (value: string, isValid: boolean) => void;
  onSend?: () => void;
  onClose?: () => void;
  onThankYou?: () => void;
};

export const CallMeModal: React.FC<CallMeModalProps> = ({ open, onClose, onPhoneChange, onSend, onThankYou }) => {
  const [formVisible, setFormVisible] = useState(true);
  const [phoneError, setPhoneError] = useState<string | undefined>(undefined);
  const [callMeMutation, { loading: callMeMutationLoading }] = useCallMe();
  const { soId } = useGeneralSoContext();

  const initialPhoneNumber = '';
  const initialCountryCode = '+385';

  function getCountryCode(phoneNumber?: string) {
    if (!phoneNumber) {
      return undefined;
    }
    const match = phoneNumber.match(/^\+\d{1,3}/);
    return match ? match[0] : undefined;
  }

  const [phoneNumber, setPhoneNumber] = useState<string>(initialPhoneNumber || '');
  const [countryCode, setCountryCode] = useState<string>(getCountryCode(initialPhoneNumber) || initialCountryCode);

  const validations: Validation[] = [
    { type: 'required', message: 'Potreban unos' },
    { type: 'validPhoneNumber', message: 'Unesi ispravan broj mobitela', params: { regionCode: 'countryCode' } },
  ]

  // const [callMeMutation] = useMutation(callMe);

  useEffect(() => {
    setPhoneNumber(initialPhoneNumber || '');
    setCountryCode(initialCountryCode || getCountryCode(initialPhoneNumber) || '');
  }, [initialPhoneNumber, initialCountryCode]);

  if (!open) {
    return null;
  }

  const validatePhoneNumber = (phone: string, code: string) => {
    let isValid = true;
    let errorMessage;

    for (const validation of validations) {
      let params: { [key: string]: any } | undefined;

      if (validation.params) {
        params = Object.keys(validation.params).reduce((acc, key) => {
          const paramKey = validation.params![key];
          if (paramKey in { phone, code }) {
            acc[key] = (params as any)[paramKey];
          }
          return acc;
        }, {} as { [key: string]: any });
      }

      if (!validationRules[validation.type](phone, params)) {
        isValid = false;
        errorMessage = validation.message;
        break;
      }
    }

    setPhoneError(errorMessage);
    if (onPhoneChange) onPhoneChange(phone, isValid);

    return isValid;
  };

  const handleSend = async () => {
    const isValid = validatePhoneNumber(phoneNumber, countryCode);
    if (!isValid) {
      return;
    }

    const data: CallMeInput = {
      product: 'loans',
      number: phoneNumber,
      soId: soId
    }
    
    await callMeMutation(data);
    
    setFormVisible(false);
    if (onSend) onSend();
  }

  const handlePhoneChange = (value: { phoneNumber: string; countryCode: string; }) => {
    setPhoneNumber(value.phoneNumber);
    setCountryCode(value.countryCode);
    const isValid = validatePhoneNumber(value.phoneNumber, value.countryCode);
    if (onPhoneChange) onPhoneChange(value.phoneNumber, isValid);
  };

  const thankYouButtonClick = () => {
    if (onThankYou) onThankYou();
    window.location.href = 'https://kompare.hr';
  }

  const htmlTextLinks: Array<HtmlTextLink> = [
    { value: 'form', link: 'https://kompare.hr/obrazac-za-prituzbe/', translation: i18n.t('components.modals.callMeModal.htmlTextLinkTranslations.form') },
    { value: 'mail', link: 'mailto:szop@kompare.hr', translation: 'szop@kompare.hr' },
    { value: 'here', link: 'https://kompare.hr/informacije-o-koristenju-osobnih-podataka/', translation: i18n.t('components.modals.callMeModal.htmlTextLinkTranslations.here') },
  ];

  return (
    <BaseModal open={open} onClose={() => onClose && onClose()} maxWidth={480} ariaLabel={i18n.t('components.modals.callMeModal.title')}>

        {formVisible &&
          <Flex vertical gap={24}>
            <CallMeModalTitle>
              <Text type='large' bold>{i18n.t('components.modals.callMeModal.title')}</Text>
            </CallMeModalTitle>
            <InputPhoneNumber label={i18n.t('components.modals.callMeModal.phone')} onChange={handlePhoneChange} value={phoneNumber} error={phoneError} />
            <Flex gap={24}>
              <Button text={i18n.t('components.modals.callMeModal.send')} className="btnBlock" onClick={handleSend} disabled={callMeMutationLoading}/>
              <Button text={i18n.t('components.modals.callMeModal.close')} btnType="textBlack" onClick={onClose} />
            </Flex>
            <Divider />
            <Flex gap={8}>
              <IconInfo width={12} height={12} color={colors.icon5} />
              <HtmlText i18nKey="components.modals.callMeModal.gdpr" links={htmlTextLinks} textSize="micro" />
            </Flex>
          </Flex>
        }

        {!formVisible &&
          <Flex vertical gap={24}>
            <CallMeModalTitle>
              <Flex gap={16}>
                <IconPhoneCall color={colors.primary} width={32} height={32} />
                <Text type='large' bold>{i18n.t('components.modals.callMeModal.thankYou')}</Text>
              </Flex>
            </CallMeModalTitle>
            <Text type="medium" bold>{i18n.t('components.modals.callMeModal.contactSoon')}</Text>
            <Text type="base" color={colors.text2}>{i18n.t('components.modals.callMeModal.contactSoon2')}</Text>
            <Button
              text={i18n.t('components.modals.callMeModal.returnToHome')}
              btnType="outlineBlack"
              onClick={thankYouButtonClick} />
          </Flex>
        }

    </BaseModal>
  );
};